import React, { useMemo } from 'react';
import {
  CardLegalStatusProps,
  CardLegalStatusVariant,
  CardLegalStatusVariantStyles,
} from './CardLegalStatus.types';
import { Badge, BadgeStatus } from '../Badge/Badge';
import { BadgeStatusType } from '../Badge/BadgeTypes';
import { APP_COLORS } from '../../config/colors';
import { SVGIcon } from '../SVGIcon';

/**
 * Konfiguracja stylów dla różnych wariantów CardLegalStatus
 */
const getVariantStyles = (
  variant: CardLegalStatusVariant,
): CardLegalStatusVariantStyles => {
  switch (variant) {
    case 'success':
      return {
        containerBg: 'bg-legalStatus-successBg',
        containerBorder: 'border-legalStatus-successBorder',
        iconBg: 'bg-green-200',
        iconColor: APP_COLORS.green[700],
        badgeVariant: 'success',
        textColor: 'text-text-primary',
      };
    case 'info':
      return {
        containerBg: 'bg-white',
        containerBorder: 'border-grayBorder',
        iconBg: 'bg-primary-100',
        iconColor: APP_COLORS.blue.DEFAULT,
        badgeVariant: 'info',
        textColor: 'text-text-primary',
      };
    case 'danger':
      return {
        containerBg: 'bg-legalStatus-dangerBg',
        containerBorder: 'border-legalStatus-dangerBorder',
        iconBg: 'bg-red-200',
        iconColor: APP_COLORS.red[800],
        badgeVariant: 'error',
        textColor: 'text-legalStatus-dangerText',
      };
    default:
      return getVariantStyles('success');
  }
};

/**
 * Komponent CardLegalStatus - wyświetla status prawny pojazdu
 *
 * @param variant - success (czysty) lub danger (problematyczny)
 * @param title - tytuł karty
 * @param description - opis na dole karty
 * @param iconName - nazwa ikony (opcjonalna)
 * @param badgeText - tekst na badge (opcjonalny)
 * @param className - dodatkowe klasy CSS
 *
 * @example
 * <CardLegalStatus
 *   variant="success"
 *   title="Bazy pojazdów skradzionych"
 *   description="Pojazd nie figuruje w bazach pojazdów kradzionych."
 *   iconName="check"
 * />
 */
export const CardLegalStatus: React.FC<CardLegalStatusProps> = ({
  variant,
  title,
  description,
  iconName,
  // iconName = variant === 'success' ? 'check' : 'warning',
  badgeText,
  className = '',
}) => {
  const styles = useMemo(() => getVariantStyles(variant), [variant]);
  const badgeStatus = useMemo(() => {
    switch (variant) {
      case 'success':
        return BadgeStatusType.Success;
      case 'danger':
        return BadgeStatusType.Error;
      case 'info':
        return BadgeStatusType.Info;
      default:
        return BadgeStatusType.Success;
    }
  }, [variant]);
  const defaultBadgeText = variant === 'success' ? 'Czysty' : 'Problematyczny';
  const finalBadgeText = badgeText || defaultBadgeText;

  return (
    <div
      className={`${styles.containerBg} ${styles.containerBorder} rounded-card border px-5 py-7 ${className}`}
    >
      <div className="flex flex-col justify-between items-start mb-8 md:items-center md:flex-row">
        <div className="flex gap-6 items-center">
          <div className="relative">
            <div
              className={`${styles.iconBg} flex h-10 w-10 items-center justify-center rounded-[10px]`}
            >
              {variant === 'danger' && (
                <SVGIcon name="document-task" fill="red-800" />
              )}
              {variant === 'success' && (
                <SVGIcon name={iconName || 'file-add'} fill="green-700" />
              )}
              {variant === 'info' && <SVGIcon name="file-add" fill="blue" />}
            </div>
            {variant === 'success' && (
              <SVGIcon
                name={'check-circle'}
                fill='white'
                className='absolute right-[-10px] top-[-10px] w-5 h-5'
              />)}
            {variant === 'danger' && (
              <SVGIcon
                name={'warn-circle'}
                fill='white'
                className='absolute right-[-10px] top-[-10px] w-5 h-5'
              />
            )}
          </div>

          <h3 className="max-w-[370px] font-inter text-[22px] font-bold leading-[28px] text-text-primary">
            {title}
          </h3>
        </div>

        <div className="flex flex-col gap-2 mt-4 mb-auto md:items-end md:mt-0">
          <Badge variant={badgeStatus} badgeText={finalBadgeText} version='v2' classNames='font-bold' />
        </div>
      </div>

      {/* Opis */}
      <div
        className={`${styles.textColor} max-w-[580px] font-inter text-sm leading-[22px]`}
      >
        {description}
      </div>
    </div>
  );
};
